import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DebugLoggerService } from './debug-logger.service';
import { DEFAULT_WEBRTC_CONFIG } from '../models/webrtc.models';

/** Same HTTP API host as ServerSyncService */
const SERVER_URL = 'https://ws.aforo.bink.tools:8080';

interface TurnCredentialsResponse {
  username: string;
  credential: string;
  ttl: number;
  uris: string[];
}

interface CachedTurnCredentials {
  iceServer: RTCIceServer;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class TurnCredentialsService {
  private readonly http = inject(HttpClient);
  private readonly debugLogger = inject(DebugLoggerService);

  private cache: CachedTurnCredentials | null = null;
  private inFlight: Promise<RTCIceServer[] | null> | null = null;

  /**
   * Returns STUN + TURN iceServers. Falls back to STUN-only on failure.
   */
  async getIceServers(): Promise<RTCIceServer[]> {
    const stunServers = [...DEFAULT_WEBRTC_CONFIG.iceServers];

    try {
      const turnServer = await this.fetchTurnIceServer();
      if (turnServer) {
        return [...stunServers, turnServer];
      }
    } catch (error) {
      this.debugLogger.warn('TURN', 'Failed to obtain TURN credentials; using STUN-only', error);
    }

    return stunServers;
  }

  private async fetchTurnIceServer(): Promise<RTCIceServer | null> {
    const now = Date.now();
    if (this.cache && this.cache.expiresAt > now) {
      return this.cache.iceServer;
    }

    if (this.inFlight) {
      await this.inFlight;
      return this.cache?.iceServer ?? null;
    }

    this.inFlight = this.loadCredentials();
    try {
      await this.inFlight;
      return this.cache?.iceServer ?? null;
    } finally {
      this.inFlight = null;
    }
  }

  private async loadCredentials(): Promise<RTCIceServer[] | null> {
    const response = await firstValueFrom(
      this.http.get<TurnCredentialsResponse>(`${SERVER_URL}/api/turn-credentials`)
    );

    if (!response?.username || !response?.credential || !Array.isArray(response.uris) || response.uris.length === 0) {
      this.debugLogger.warn('TURN', 'Invalid turn-credentials response', response);
      return null;
    }

    const ttlMs = Math.max(60_000, (response.ttl || 86400) * 1000);
    // Refresh at 80% of TTL so credentials do not expire mid-session
    const expiresAt = Date.now() + Math.floor(ttlMs * 0.8);

    const iceServer: RTCIceServer = {
      urls: response.uris,
      username: response.username,
      credential: response.credential
    };

    this.cache = { iceServer, expiresAt };
    this.debugLogger.log('TURN', 'TURN credentials cached', {
      uris: response.uris,
      ttl: response.ttl,
      expiresAt: new Date(expiresAt).toISOString()
    });

    return [iceServer];
  }
}
